<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edmetadata: { type: Object, default: () => ({}) },
    edaiInstructions: { type: String, default: '' },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      templateIdentifier: this.edmetadata?.template_identifier || '',
      shortCode: this.edshortCode,
      content: this.edcontent,
      problemTag: this.edmetadata?.problem_tag || '',
      actionTag: this.edmetadata?.action_tag || '',
      category: this.edmetadata?.category || '',
      aiInstructions: this.edaiInstructions || '',
      show: true,
      problemTags: [
        '+14d Shipping Paid',
        '+14d Shipping Free',
        'Missed Delivery',
        'Returned Order',
        'Order Lost',
        'Order Destroyed',
        'Claim No Delivered',
        'Edit Items Order',
        'Shipping Address',
        'Cancel Order',
        'Item Broken',
        'Wrong Design',
        'Incomplete Order',
        'Find Too "Cheap"',
        'Lamp Ineffective',
        'Doesnt like colors',
        'Order Confirmation',
        'Chargeback Contacted',
        'Ask Remove Chargeback',
        'Issue Resolved'
      ],
      actionTags: [
        'Refund Shipping',
        'Hold Order',        
        'Refund -10%',
        'Refund -30%',
        'Refund -50%',
        'Refund -100%',        
        'Reship',
        'Check Tracking',
        'Edit Items Order',
        'Edit Shipping Address',
        'Wait Return',
        'Refund Lamp Price',
        'Send Order Confirmation',
        'Delete Order Issue'
      ],
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.templateIdentifier = '';
      this.shortCode = '';
      this.content = '';
      this.problemTag = '';
      this.actionTag = '';
      this.category = '';
      this.aiInstructions = '';
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    extractVariables(content) {
      const regex = /\{([^}]+)\}/g;
      const matches = content.matchAll(regex);
      const variables = [];
      for (const match of matches) {
        if (!variables.includes(match[1])) {
          variables.push(match[1]);
        }
      }
      return variables;
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      
      // Prepare metadata
      const metadata = {
        template_identifier: this.templateIdentifier,
        problem_tag: this.problemTag,
        action_tag: this.actionTag,
        category: this.category,
        variables: this.extractVariables(this.content),
        ai_instructions: this.aiInstructions,  // Save AI instructions in metadata
      };
      
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          metadata: metadata,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="flex gap-4 w-full">
          <div class="flex-1">
            <label>
              Template ID
              <span class="text-xs text-slate-500">(e.g., TMPL-001)</span>
              <input
                v-model="templateIdentifier"
                type="text"
                placeholder="TMPL-001"
              />
            </label>
          </div>

          <div class="flex-1">
            <label :class="{ error: v$.shortCode.$error }">
              {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
              <input
                v-model="shortCode"
                type="text"
                :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
                @input="v$.shortCode.$touch"
              />
            </label>
          </div>
        </div>

        <div class="flex gap-4 w-full">
          <div class="flex-1">
            <label>
              Category
              <input
                v-model="category"
                type="text"
                placeholder="e.g., Customer Refund"
              />
            </label>
          </div>

          <div class="flex-1">
            <!-- Empty space for alignment -->
          </div>
        </div>

        <div class="flex gap-4 w-full">
          <div class="flex-1">
            <label>
              Problem Tag
              <select v-model="problemTag" class="w-full">
                <option value="">Select a problem tag...</option>
                <option v-for="tag in problemTags" :key="tag" :value="tag">
                  {{ tag }}
                </option>
              </select>
            </label>
          </div>

          <div class="flex-1">
            <label>
              Action Tag
              <select v-model="actionTag" class="w-full">
                <option value="">Select an action tag...</option>
                <option v-for="tag in actionTags" :key="tag" :value="tag">
                  {{ tag }}
                </option>
              </select>
            </label>
          </div>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
          <p class="text-xs text-slate-600 mt-1">
            Use {variable_name} for variables (e.g., {customer_name}, {order_number})<br>
            For links: Plain URLs auto-link, or use {link:https://example.com|Click here}
          </p>
        </div>

        <div class="w-full">
          <label>
            Agent Instructions
            <span class="text-xs text-slate-500">
              (Describe when to use this template)
            </span>
          </label>
          <textarea
            v-model="aiInstructions"
            rows="4"
            placeholder="Use this when customer reports a damaged product. Ask for photo evidence. Only if order number is provided in the conversation."
            class="w-full p-2 border border-slate-200 rounded resize-y"
          />
          <p class="text-xs text-slate-600 mt-1">
            This helps agents understand when to use this template
          </p>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
